import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CloudUpload, FileText, Trash2, CheckCircle } from 'lucide-react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'

export default function BusinessVerificationUploadPage() {
  const { applicationId } = useParams()
  const navigate = useNavigate()

  const [documents, setDocuments] = useState([])
  const [uploading, setUploading] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    handleGetDocuments()
  }, [])

  const handleGetDocuments = async () => {
    const response = await fetch(`${apiURL}/api/applications/${applicationId}/business-verification`, {
      method: 'GET',
      headers: {
        'Authorization': account.accessToken
      }
    })
    const data = await response.json()
    if (data.response.error) {
      if (data.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken && refreshedToken.length > 0)
          await handleGetDocuments()
        else
          navigate('/', { state: { expired: true } })
      } else
        console.error(data.response.error)
    } else
      setDocuments(data.response.documents || [])
  }

  const handleUploadFile = async (file) => {
    setUploading(true)
    setError('')

    const formData = new FormData()
    formData.append('file', file)

    const uploadResponse = await fetch(`${apiURL}/api/applications/${applicationId}/business-verification`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken
      },
      body: formData
    })

    const uploadData = await uploadResponse.json()
    if (uploadData.response.error) {
      if (uploadData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken && refreshedToken.length > 0)
          await handleUploadFile(file)
        else
          navigate('/', { state: { expired: true } })
      } else
        setError(uploadData.response.error)
    } else {
      setDocuments(prev => [...prev, {
        documentId: uploadData.response.documentId,
        name: file.name,
        size: file.size,
        mimetype: file.type,
        documentURL: uploadData.response.documentURL
      }])
    }

    setUploading(false)
  }

  const handleFileSelect = (e) => {
    const files = e.target.files
    if (!files) return
    Array.from(files).forEach(async file => {
      await handleUploadFile(file)
    })
    e.target.value = ''
  }

  const handleComplete = async () => {
    setCompleting(true)
    setError('')

    const completeResponse = await fetch(`${apiURL}/api/applications/${applicationId}/business-verification/complete`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      }
    })

    const completeData = await completeResponse.json()
    if (completeData.response.error) {
      if (completeData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken && refreshedToken.length > 0)
          await handleComplete()
        else
          navigate('/', { state: { expired: true } })
      } else
        setError(completeData.response.error)
    } else {
      const ok = completeData.response === 'OK' || completeData.response?.success
      if (ok) {
        navigate(`/applications/${applicationId}/e-sign/8821`)
      }
    }

    setCompleting(false)
  }

  const isComplete = documents.length > 0

  return (
    <div className='w-full px-4 sm:px-8 flex flex-col items-center py-8 text-slate-900 dark:text-white transition-colors duration-300'>
      <div className='flex flex-col items-center w-full max-w-2xl space-y-6'>
        <h1 className='text-3xl font-bold text-slate-900 dark:text-white text-center'>Business Verification</h1>
        <p className='text-center text-gray-600 dark:text-slate-300 leading-relaxed'>
          Please upload a document that <span className='font-semibold text-slate-800 dark:text-gray-100'>MUST INCLUDE</span> your business name, EIN, and address.
          <br />
          Examples include: Articles of Incorporation, EIN Letter, Business License, or similar documents.
        </p>

        <div className='w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 space-y-6 transition-colors duration-300'>
          <label
            htmlFor='business-verification-upload'
            className='w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 dark:bg-slate-700/50 dark:hover:bg-slate-700 transition cursor-pointer group'
          >
            <CloudUpload size={40} className='text-gray-400 dark:text-slate-400 mb-2 group-hover:text-ertc-primary transition-colors' />
            <p className='text-md font-semibold text-gray-700 dark:text-slate-200 mb-1 group-hover:text-ertc-primary transition-colors'>
              {uploading ? 'Uploading...' : 'Upload Verification Document'}
            </p>
            <p className='text-sm text-gray-500 dark:text-slate-400'>Click to select a file or drag and drop</p>
          </label>
          <input
            id='business-verification-upload'
            type='file'
            className='hidden'
            onChange={handleFileSelect}
            disabled={uploading}
          />

          {documents.length > 0 && (
            <div className='space-y-3'>
              <h3 className='text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider'>Uploaded Documents</h3>
              {documents.map(doc => (
                <div key={doc.documentId} className='flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg'>
                  <FileText size={20} className='text-ertc-primary flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-gray-700 dark:text-slate-200 truncate'>{doc.name}</p>
                    <p className='text-xs text-gray-500 dark:text-slate-400'>
                      {(doc.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <CheckCircle size={18} className='text-green-500 flex-shrink-0' />
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className='w-full bg-red-50 dark:bg-red-900/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm'>
            {error}
          </div>
        )}

        <button
          onClick={handleComplete}
          disabled={!isComplete || completing}
          className={`px-10 py-3 rounded-xl shadow-md text-white text-lg font-bold transition-all ${isComplete && !completing
            ? 'bg-ertc-primary hover:bg-ertc-primary/90 active:scale-95'
            : 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed shadow-none'
            }`}
        >
          {completing ? 'Submitting...' : 'Submit Verification'}
        </button>

        {completing && (
          <div className='flex flex-row items-center justify-center gap-2'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
            <p className='text-md text-center text-slate-600 dark:text-slate-300'>Completing verification...</p>
          </div>
        )}
      </div>
    </div>
  )
}
